import { Loader2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

export const Spinner = ({ className }) => <Loader2 className={cn("h-4 w-4 animate-spin", className)} />;

export const FullSpinner = ({ label }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#09090B]" data-testid="full-spinner">
    <Loader2 className="h-7 w-7 animate-spin text-[#D4AF37]" />
    {label && <p className="text-sm text-zinc-400">{label}</p>}
  </div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-100 sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-zinc-400 md:text-base">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Card = ({ className, children, ...p }) => (
  <div className={cn("rounded-xl border border-[#27272A] bg-[#18181B] p-5", className)} {...p}>
    {children}
  </div>
);

export const Caption = ({ children, className }) => (
  <div className={cn("text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500", className)}>{children}</div>
);

export const NativeSelect = ({ value, onChange, options, className, ...p }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className={cn(
      "h-10 rounded-md border border-[#3F3F46] bg-[#18181B] px-3 text-sm text-zinc-200 outline-none transition-colors focus:border-[#D4AF37]",
      className
    )}
    {...p}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

export const TierBadge = ({ tier, className }) => {
  const t = String(tier || "free").toLowerCase();
  const styles = {
    elite: "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3C649]",
    pro: "border-sky-500/40 bg-sky-500/10 text-sky-300",
    free: "border-zinc-600 bg-zinc-800 text-zinc-300",
  };
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", styles[t] || styles.free, className)}>
      {t !== "free" && <Lock className="hidden" />}
      {t}
    </span>
  );
};

export const ScoreRing = ({ score, size = 64 }) => {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const color = s >= 75 ? "#D4AF37" : s >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272A" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * s) / 100} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <span className="absolute font-mono text-sm font-bold text-zinc-100">{Math.round(s)}</span>
    </div>
  );
};

export const CompanyLogo = ({ src, name, size = "h-11 w-11" }) =>
  src ? (
    <img src={src} alt={name} className={cn(size, "shrink-0 rounded-lg border border-[#27272A] bg-white object-contain p-1")} onError={(e) => (e.currentTarget.style.display = "none")} />
  ) : (
    <div className={cn(size, "flex shrink-0 items-center justify-center rounded-lg border border-[#D4AF37]/25 bg-[#27272A] font-display font-bold text-[#D4AF37]")}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );

export const Empty = ({ icon: Icon, title, text, children }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#3F3F46] px-6 py-16 text-center" data-testid="empty-state">
    {Icon && <Icon className="mb-4 h-8 w-8 text-[#D4AF37]" />}
    <h3 className="font-display text-lg text-zinc-200">{title}</h3>
    {text && <p className="mt-1 max-w-md text-sm text-zinc-500">{text}</p>}
    {children && <div className="mt-5">{children}</div>}
  </div>
);

export const Rating = ({ value, onChange, testid, labels }) => (
  <div className="flex gap-2" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`} title={labels?.[n - 1]}
        className={cn("h-10 w-10 rounded-lg border font-mono text-sm transition-colors",
          value === n ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-[#3F3F46] bg-[#09090B] text-zinc-400 hover:border-[#D4AF37]/60")}>
        {n}
      </button>
    ))}
  </div>
);
